import { useEffect, useState } from "react";
import { Loader2, Search } from "lucide-react";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@project/components/ui/radio-group";
import { Textarea } from "@project/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import { SearchableSelect } from "@project/admin/components/searchable-select";
import { donationDonorService } from "@project/services/admin/donation-donor.service";
import { donationMasterService } from "@project/services/admin/donation-master.service";
import { locationService } from "@project/services/admin/location.service";
import { projectToast } from "@project/lib/toast";
import type { Area, City, District } from "@project/types/location";
import { PAYMENT_MODE_OPTIONS } from "@project/types/donation-donor";
import { MobileNumberField } from "@project/components/mobile-number-field";
import {
  DEFAULT_PHONE_COUNTRY,
  isValidMobileNumber,
  type PhoneCountryCode,
} from "@project/lib/mobile-number";

export type DonorFormState = Record<string, unknown>;

type DonorFormFieldsProps = {
  editing: DonorFormState | null;
  setEditing: React.Dispatch<React.SetStateAction<DonorFormState | null>>;
};

function patch(
  editing: DonorFormState | null,
  setEditing: DonorFormFieldsProps["setEditing"],
  updates: DonorFormState
) {
  setEditing({ ...(editing ?? {}), ...updates });
}

export function DonorFormFields({ editing, setEditing }: DonorFormFieldsProps) {
  const [districts, setDistricts] = useState<District[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingAreas, setLoadingAreas] = useState(false);
  const [searchingHead, setSearchingHead] = useState(false);

  const districtId = String(editing?.district ?? "");
  const cityId = String(editing?.city ?? "");
  const headMode = editing?.is_already_head ? "already_head" : "head";
  const isAlreadyHead = headMode === "already_head";

  useEffect(() => {
    let ignore = false;
    const loadDistricts = async () => {
      setLoadingDistricts(true);
      try {
        const result = await locationService.listDistrictsPage({
          page: 1,
          page_size: 200,
          status: "active",
        });
        if (!ignore) {
          setDistricts(result.data);
        }
      } catch {
        if (!ignore) {
          projectToast.error("Failed to load districts");
        }
      } finally {
        if (!ignore) {
          setLoadingDistricts(false);
        }
      }
    };
    void loadDistricts();
    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    if (!districtId) {
      setCities([]);
      return;
    }
    let ignore = false;
    const loadCities = async () => {
      setLoadingCities(true);
      try {
        const result = await locationService.listCitiesPage({
          page: 1,
          page_size: 200,
          district: Number(districtId),
          status: "active",
        });
        if (!ignore) {
          setCities(result.data);
        }
      } catch {
        if (!ignore) {
          projectToast.error("Failed to load cities");
        }
      } finally {
        if (!ignore) {
          setLoadingCities(false);
        }
      }
    };
    void loadCities();
    return () => {
      ignore = true;
    };
  }, [districtId]);

  useEffect(() => {
    if (!cityId) {
      setAreas([]);
      return;
    }
    let ignore = false;
    const loadAreas = async () => {
      setLoadingAreas(true);
      try {
        const result = await locationService.listAreasPage({
          page: 1,
          page_size: 200,
          city: Number(cityId),
          status: "active",
        });
        if (!ignore) {
          setAreas(result.data);
        }
      } catch {
        if (!ignore) {
          projectToast.error("Failed to load areas");
        }
      } finally {
        if (!ignore) {
          setLoadingAreas(false);
        }
      }
    };
    void loadAreas();
    return () => {
      ignore = true;
    };
  }, [cityId]);

  const handleHeadSearch = async () => {
    const headPhone = String(editing?.head_phone ?? "").trim();
    const headCountry =
      (editing?.head_phone_country as PhoneCountryCode | undefined) ??
      DEFAULT_PHONE_COUNTRY;
    if (!isValidMobileNumber(headPhone, headCountry)) {
      projectToast.error(
        "Please enter a valid head mobile number for the selected country"
      );
      return;
    }

    setSearchingHead(true);
    try {
      const donor = await donationDonorService.searchByPhone(headPhone);
      if (!donor) {
        patch(editing, setEditing, {
          is_head: false,
          is_already_head: true,
          head_phone: headPhone,
          head_name: "",
          head_found_phone: "",
        });
        projectToast.info("No head donor found for this mobile number");
        return;
      }

      patch(editing, setEditing, {
        is_head: false,
        is_already_head: true,
        head_phone: headPhone,
        head_name: donor.name ?? "",
        head_found_phone: donor.phone ?? headPhone,
      });
      projectToast.success("Head donor found.");
    } catch {
      projectToast.error("Error searching head donor");
    } finally {
      setSearchingHead(false);
    }
  };

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="sm:col-span-2">
        <Label data-required="true">Head Status</Label>
        <RadioGroup
          value={headMode}
          onValueChange={value =>
            patch(
              editing,
              setEditing,
              value === "already_head"
                ? { is_head: false, is_already_head: true }
                : {
                    is_head: true,
                    is_already_head: false,
                    head_phone: "",
                    head_name: "",
                    head_found_phone: "",
                  }
            )
          }
          className="border-border/70 mt-2 grid overflow-hidden rounded-xl border bg-white sm:grid-cols-2 sm:divide-x sm:divide-border"
        >
          <label className="flex cursor-pointer items-center gap-2 px-4 py-3 text-sm font-medium">
            <RadioGroupItem value="head" />I am the Family Head
          </label>
          <label className="flex cursor-pointer items-center gap-2 px-4 py-3 text-sm font-medium">
            <RadioGroupItem value="already_head" />A Family Head Already Exists
          </label>
        </RadioGroup>
      </div>

      {isAlreadyHead && (
        <div className="sm:col-span-2">
          <Label data-required="true">Head Mobile Number</Label>
          <div className="mt-1 flex gap-2">
            <MobileNumberField
              className="min-w-0 flex-1"
              value={String(editing?.head_phone ?? "")}
              country={
                (editing?.head_phone_country as PhoneCountryCode | undefined) ??
                DEFAULT_PHONE_COUNTRY
              }
              onCountryChange={code =>
                patch(editing, setEditing, {
                  head_phone_country: code,
                  head_name: "",
                  head_found_phone: "",
                })
              }
              onChange={value =>
                patch(editing, setEditing, {
                  head_phone: value,
                  head_name: "",
                  head_found_phone: "",
                })
              }
              placeholder="Head mobile"
            />
            <Button
              type="button"
              onClick={handleHeadSearch}
              disabled={searchingHead}
            >
              {searchingHead ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
              Search
            </Button>
          </div>
          {editing?.head_name ? (
            <div className="border-primary/40 bg-primary/5 mt-3 rounded-xl border px-4 py-3 text-sm">
              <p className="font-semibold text-foreground">
                {String(editing.head_name)}
              </p>
              <p className="mt-1 text-muted-foreground">
                Mobile:{" "}
                <span className="font-medium text-foreground">
                  {String(editing.head_found_phone ?? editing.head_phone ?? "")}
                </span>
              </p>
            </div>
          ) : null}
        </div>
      )}

      <div className="sm:col-span-2">
        <Label data-required="true">Full Name</Label>
        <Input
          value={String(editing?.name ?? "")}
          onChange={e => patch(editing, setEditing, { name: e.target.value })}
          placeholder="Suresh Patel"
        />
      </div>

      <div>
        <Label data-required="true">Email</Label>
        <Input
          type="email"
          value={String(editing?.email ?? "")}
          onChange={e => patch(editing, setEditing, { email: e.target.value })}
          placeholder="suresh@example.com"
        />
      </div>

      <div>
        <Label data-required="true">Phone</Label>
        <MobileNumberField
          value={String(editing?.phone ?? "")}
          country={
            (editing?.phone_country as PhoneCountryCode | undefined) ??
            DEFAULT_PHONE_COUNTRY
          }
          onCountryChange={code =>
            patch(editing, setEditing, { phone_country: code })
          }
          onChange={value => patch(editing, setEditing, { phone: value })}
          placeholder="Mobile number"
        />
      </div>

      <div className="sm:col-span-2">
        <Label>Organization</Label>
        <Input
          value={String(editing?.organization ?? "")}
          onChange={e =>
            patch(editing, setEditing, { organization: e.target.value })
          }
          placeholder="Patel Foundation"
        />
      </div>

      <div>
        <Label data-required="true">Donation Type</Label>
        <SearchableSelect
          value={String(editing?.donation_type ?? "")}
          onValueChange={value =>
            patch(editing, setEditing, { donation_type: value })
          }
          onSearch={async query => {
            const options =
              await donationMasterService.listDonationTypesForLookup(query);
            return options.map(option => ({
              value: option.label,
              label: option.label,
            }));
          }}
          seedOption={
            editing?.donation_type
              ? {
                  value: String(editing.donation_type),
                  label: String(editing.donation_type),
                }
              : undefined
          }
          placeholder="Select donation type"
          searchPlaceholder="Search donation types..."
          emptyText="No donation types found."
        />
      </div>

      <div>
        <Label data-required="true">Amount (₹)</Label>
        <Input
          type="number"
          min="0"
          step="0.01"
          value={String(editing?.amount ?? "")}
          onChange={e => patch(editing, setEditing, { amount: e.target.value })}
          placeholder="5000.00"
        />
      </div>

      <div>
        <Label data-required="true">Payment Mode</Label>
        <Select
          value={String(editing?.payment_mode ?? "") || undefined}
          onValueChange={value =>
            patch(editing, setEditing, { payment_mode: value })
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Select payment mode" />
          </SelectTrigger>
          <SelectContent>
            {PAYMENT_MODE_OPTIONS.map(mode => (
              <SelectItem key={mode.value} value={mode.value}>
                {mode.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label data-required="true">PAN Number</Label>
        <Input
          value={String(editing?.pan_number ?? "")}
          onChange={e =>
            patch(editing, setEditing, {
              pan_number: e.target.value.toUpperCase(),
            })
          }
          placeholder="ABCDE1234F"
          maxLength={10}
          className="uppercase"
        />
      </div>

      <div className="sm:col-span-2">
        <Label>Ref No. / Cheque No.</Label>
        <Input
          value={String(editing?.by_cheque_no ?? "")}
          onChange={e =>
            patch(editing, setEditing, { by_cheque_no: e.target.value })
          }
          placeholder="CHQ-123456"
        />
      </div>

      <div className="sm:col-span-2">
        <Label>Message</Label>
        <Textarea
          value={String(editing?.message ?? "")}
          onChange={e =>
            patch(editing, setEditing, { message: e.target.value })
          }
          placeholder="Regular donor"
          rows={2}
        />
      </div>

      <div className="sm:col-span-2">
        <Label data-required="true">Address Line 1</Label>
        <Input
          value={String(editing?.address_line1 ?? "")}
          onChange={e =>
            patch(editing, setEditing, { address_line1: e.target.value })
          }
          placeholder="12 Shanti Nagar"
        />
      </div>

      <div>
        <Label data-required="true">District</Label>
        <Select
          value={districtId || undefined}
          onValueChange={value =>
            patch(editing, setEditing, { district: value, city: "", area: "" })
          }
          disabled={loadingDistricts}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select district" />
          </SelectTrigger>
          <SelectContent>
            {districts.map(d => (
              <SelectItem key={d.id} value={String(d.id)}>
                {d.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label data-required="true">City</Label>
        <Select
          value={cityId || undefined}
          onValueChange={value =>
            patch(editing, setEditing, { city: value, area: "" })
          }
          disabled={!districtId || loadingCities}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select city" />
          </SelectTrigger>
          <SelectContent>
            {cities.map(c => (
              <SelectItem key={c.id} value={String(c.id)}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label data-required="true">Area</Label>
        <Select
          value={String(editing?.area ?? "") || undefined}
          onValueChange={value => patch(editing, setEditing, { area: value })}
          disabled={!cityId || loadingAreas}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select area" />
          </SelectTrigger>
          <SelectContent>
            {areas.map(a => (
              <SelectItem key={a.id} value={String(a.id)}>
                {a.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label data-required="true">Pincode</Label>
        <Input
          value={String(editing?.pincode ?? "")}
          onChange={e =>
            patch(editing, setEditing, {
              pincode: e.target.value.replace(/[^0-9]/g, ""),
            })
          }
          placeholder="380015"
          maxLength={6}
        />
      </div>
    </div>
  );
}
